import React, { useMemo, useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import LoaderIndex from "~/containers/LoaderIndex";
import Volby2026Nav from "~/components/elections/volby2026/Volby2026Nav";
import ElectionProgressBar from "~/components/elections/volby2026/ElectionProgressBar";
import ElectionStats from "~/components/elections/volby2026/ElectionStats";
import RegionChairCard from "~/components/elections/volby2026/RegionChairCard";
import KrajObvodFilter from "~/components/elections/volby2026/KrajObvodFilter";
import { formatNumber, formatPercent, statusLabel } from "~/utility/volby2026";

const toArray = (x: any) => (Array.isArray(x) ? x : Object.values(x || {}));

const paginationComponentOptions = {
  rowsPerPageText: "Výsledkov na stranu",
  rangeSeparatorText: "z",
  selectAllRowsItem: true,
  selectAllRowsItemText: "Zobraziť všetko",
};

const Volby2026OskPage = (props: { legend: any; results: any }) => {
  const legend = props.legend || {};
  const results = props.results || {};

  const legendRegions = useMemo(() => toArray(legend.regions), [legend]);
  const legendConstituencies = useMemo(() => toArray(legend.constituencies), [legend]);
  const resultRegions = useMemo(() => toArray(results.regions), [results]);
  const resultConstituencies = useMemo(() => toArray(results.constituencies), [results]);

  const regions = useMemo(
    () =>
      legendRegions.map((r: any) => ({
        code: r.code,
        name: r.name,
        result: resultRegions.find((rr: any) => rr.code === r.code) || null,
      })),
    [legendRegions, resultRegions]
  );

  const [chairRegion, setChairRegion] = useState<string | null>(null);
  const [councilRegion, setCouncilRegion] = useState<string | null>(null);
  const [councilConstituency, setCouncilConstituency] = useState<string | null>(null);
  const [councilView, setCouncilView] = useState<"elected" | "substitute">("elected");

  useEffect(() => {
    if (!chairRegion && regions.length) setChairRegion(regions[0].code);
    if (!councilRegion && regions.length) setCouncilRegion(regions[0].code);
  }, [regions]);

  const constituenciesForRegion = (regionCode: string | null) =>
    legendConstituencies
      .filter((c: any) => c.region_code === regionCode)
      .map((c: any) => ({ code: c.code, name: c.name }));

  useEffect(() => {
    const options = constituenciesForRegion(councilRegion);
    if (options.length && !options.find((o) => o.code === councilConstituency)) {
      setCouncilConstituency(options[0].code);
    }
    if (!options.length) setCouncilConstituency(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [councilRegion, legendConstituencies.length]);

  const selectedChairRegionResult = resultRegions.find((r: any) => r.code === chairRegion);
  const chairCandidates = selectedChairRegionResult?.chair_candidates || [];
  const chairFinal = !!selectedChairRegionResult?.final;

  const selectedConstituencyResult = resultConstituencies.find(
    (c: any) => c.code === councilConstituency
  );
  const constituencyCandidates = selectedConstituencyResult?.candidates || [];
  const constituencyFinal = !!selectedConstituencyResult?.final;

  const electedAndSubstitutes = constituencyCandidates.filter((c: any) =>
    councilView === "elected" ? c.status === "elected" : c.status === "substitute"
  );

  const summary = results.summary || {};

  const columnsChair = [
    { name: "Poradie na hlasovacom lístku", selector: (row: any) => row.ballot_no, sortable: true },
    { name: "Meno", selector: (row: any) => row.first_name, sortable: true },
    { name: "Priezvisko", selector: (row: any) => row.last_name, sortable: true },
    { name: "Politický subjekt", selector: (row: any) => row.parties, sortable: true },
    { name: "Počet platných hlasov", selector: (row: any) => row.votes, sortable: true },
    { name: "Podiel platných hlasov v %", selector: (row: any) => formatPercent(row.votes_pct), sortable: true },
    { name: "Kandidát", selector: (row: any) => statusLabel(row.status, chairFinal), sortable: true },
  ];

  const columnsCouncilElected = [
    { name: "Poradie na hlasovacom listku", selector: (row: any) => row.ballot_no, sortable: true },
    { name: "Meno", selector: (row: any) => row.first_name, sortable: true },
    { name: "Priezvisko", selector: (row: any) => row.last_name, sortable: true },
    { name: "Politický subjekt", selector: (row: any) => row.parties, sortable: true },
    { name: "Počet platných hlasov", selector: (row: any) => row.votes, sortable: true },
  ];

  const columnsCouncilAll = [
    ...columnsCouncilElected,
    { name: "Podiel platných hlasov v %", selector: (row: any) => formatPercent(row.votes_pct), sortable: true },
    { name: "Kandidát", selector: (row: any) => statusLabel(row.status, constituencyFinal), sortable: true },
  ];

  return (
    <div>
      <div className="container static-page elections">
        <h1>VOĽBY DO ORGÁNOV SAMOSPRÁVNYCH KRAJOV 2026</h1>

        <Volby2026Nav active="osk" />

        {!!summary.protocols_total && (
          <ElectionProgressBar
            processed={summary.protocols_processed}
            total={summary.protocols_total}
            processedPct={summary.protocols_processed_pct}
          />
        )}

        <ElectionStats
          items={[
            { value: formatPercent(summary.turnout_pct), label: "účasť", highlight: true },
            { value: formatNumber(summary.voters_participated), label: "zúčastnených" },
            {
              value: formatPercent(summary.envelopes_cast_pct),
              label: "voličov, ktorí odovzdali obálku podľa § 24 (osobne)",
              highlight: true,
            },
            {
              value: formatNumber(summary.envelopes_cast),
              label: "voličov, ktorí odovzdali obálku podľa § 24 (osobne)",
            },
            { value: formatNumber(summary.voters_registered), label: "voličov zapísaných v zoznamoch voličov" },
            { value: formatNumber(summary.valid_ballots_chair), label: "platných hlasov spolu – predseda" },
            { value: "", label: "" },
            {
              value: formatNumber(summary.valid_ballots_council),
              label: "platných hlasov spolu – zastupiteľstvo",
            },
          ]}
        />

        <div className="elections-section">
          <h2>ZVOLENÍ PREDSEDOVIA SAMOSPRÁVNYCH KRAJOV</h2>
          <div className="v26-chair-grid">
            {regions.map((r) => (
              <RegionChairCard key={r.code} regionName={r.name} chair={r.result?.chair || null} />
            ))}
          </div>
        </div>

        <div className="elections-section">
          <h2>POČET A PODIEL PLATNÝCH HLASOV ODOVZDANÝCH PRE KANDIDÁTOV NA PREDSEDOV PODĽA ÚZEMNÉHO ČLENENIA</h2>
          <KrajObvodFilter
            regions={legendRegions}
            selectedRegion={chairRegion}
            onRegionChange={setChairRegion}
          />
          <div className="data-table-wrapper">
            <DataTable
              columns={columnsChair}
              data={chairCandidates}
              pagination
              paginationComponentOptions={paginationComponentOptions}
              noDataComponent="Žiadne výsledky"
            />
          </div>
        </div>

        <div className="elections-section">
          <h2>ZVOLENÍ POSLANCI ZASTUPITEĽSTVA A NÁHRADNÍCI</h2>
          <KrajObvodFilter
            regions={legendRegions}
            constituencies={constituenciesForRegion(councilRegion)}
            selectedRegion={councilRegion}
            selectedConstituency={councilConstituency}
            onRegionChange={setCouncilRegion}
            onConstituencyChange={setCouncilConstituency}
          />
          <div className="center-filter mb-20">
            <label>
              <input
                type="radio"
                checked={councilView === "elected"}
                onChange={() => setCouncilView("elected")}
              />{" "}
              Poslanci
            </label>
            &nbsp;&nbsp;
            <label>
              <input
                type="radio"
                checked={councilView === "substitute"}
                onChange={() => setCouncilView("substitute")}
              />{" "}
              Náhradníci
            </label>
          </div>
          <div className="data-table-wrapper">
            <DataTable
              columns={columnsCouncilElected}
              data={electedAndSubstitutes}
              noDataComponent="Žiadne výsledky"
            />
          </div>
        </div>

        <div className="elections-section">
          <h2>POČET A PODIEL PLATNÝCH HLASOV ODOVZDANÝCH PRE KANDIDÁTOV NA POSLANCOV PODĽA ÚZEMNÉHO ČLENENIA</h2>
          <KrajObvodFilter
            regions={legendRegions}
            constituencies={constituenciesForRegion(councilRegion)}
            selectedRegion={councilRegion}
            selectedConstituency={councilConstituency}
            onRegionChange={setCouncilRegion}
            onConstituencyChange={setCouncilConstituency}
          />
          <div className="data-table-wrapper">
            <DataTable
              columns={columnsCouncilAll}
              data={constituencyCandidates}
              pagination
              paginationComponentOptions={paginationComponentOptions}
              noDataComponent="Žiadne výsledky"
            />
          </div>
        </div>
      </div>

      <LoaderIndex />
    </div>
  );
};

export default Volby2026OskPage;
